const AbstractCloverRequestRecord = require('@/lib/PaymentMethods/Clover/Records/Request/AbstractCloverRequestRecord');
const CloverTransactionEndState = require('@/lib/PaymentMethods/Clover/Enums/CloverTransactionEndState');
const CloverTransactionState = require('@/lib/PaymentMethods/Clover/Enums/CloverTransactionState');

/**
 * Base class for CloverTransaction and CloverVoidTransaction.
 * These classes contain everything you need to know about the current transaction (request, response, and state).
 */
class AbstractCloverTransaction {
    constructor(transactionClassName) {
        /**
         * Used to identify which transaction class we are dealing with when it has been converted to a generic object.
         *
         * @type {string}
         */
        this.transactionClassName = transactionClassName;

        /**
         * Axios Response
         *
         * @see https://axios-http.com/docs/res_schema
         *
         * @type {Object}
         */
        this.latestResponse = null;

        /**
         * An UPPER_SNAKES_CASE string that gives the current status of the transaction e.g. IN_PROGRESS
         * See CloverTransactionState.js
         *
         * @type {?string}
         */
        this.state = null;

        this.promptForVoidReceipt = false;

        /**
         * When status === TRANSACTION_FINISHED what is the result?
         * See CloverTransactionEndState.js
         *
         * @type {{message: ?string, class: ?string}}
         */
        this.transactionResult = {
            message: null,
            class: null,
        };

        /**
         * Create a timestamp to track the moment the transaction was created.
         *
         * @type {number}
         */
        this.transactionCreatedAt = Date.now();
    }

    /**
     * @param {AbstractCloverRequestRecord} request
     */
    addTransactionRequest(request) {
        if (!request || !(request instanceof AbstractCloverRequestRecord)) {
            throw new Error('CloverTransaction requires a request that extends AbstractCloverRequestRecord');
        }

        /**
         * @type {AbstractCloverRequestRecord}
         */
        this.initialRequest = request;
        this.state = CloverTransactionState.IN_PROGRESS;
    }

    setTransactionSuccessfulVoidFailed() {
        this.transactionResult.message = CloverTransactionEndState.VOID_FAILED;
    }

    setTransactionVoided() {
        this.transactionResult.message = CloverTransactionEndState.VOID;
        this.promptForVoidReceipt = true;
    }

    isHttpResponseSuccess() {
        if (this.latestResponse.status === 200) {
            // Handle this later.
            return true;
        }
        if ([209, 503, 504].includes(this.latestResponse.status)) {
            // The Device did not respond in a timely manner. This can be repeated at a later time.
            // Likely either a timeout or cancel.
            this.transactionResult.message = CloverTransactionEndState.CANCELLED;
            this.transactionResult.class = 'message-line-danger';
            return false;
        }

        if ([400, 401, 415].includes(this.latestResponse.status)) {
            // The request was invalid. Probably a ProPoint error.
            this.transactionResult.message = CloverTransactionEndState.ERROR;
            this.transactionResult.class = 'message-line-danger';
            return false;
        }

        if ([500, 501].includes(this.latestResponse.status)) {
            // Probably a server error.
            this.transactionResult.message = CloverTransactionEndState.ERROR;
            this.transactionResult.class = 'message-line-danger';
            return false;
        }
        return false;
    }
}

module.exports = AbstractCloverTransaction;
